// lib/numericUtils.js
// Helper centralizzati per la gestione degli input numerici italiani
// Permette inserimento libero con virgola/punto e parsing solo onBlur/submit

/**
 * Parse Italian number format to float
 * Accetta: 543, 1.543, 1.543,50, 1543,50, 1543.50, 0,07, 0.07, 10,5, 10.5
 * 
 * @param {string|number} value - Il valore da parsare
 * @param {number|null} defaultValue - Valore di default se parsing fallisce (null = restituisce null)
 * @returns {number|null} - Il numero parsato o defaultValue
 */
export const parseItalianNumber = (value, defaultValue = null) => {
  // Se già un numero valido, restituisci come-è
  if (typeof value === 'number' && !isNaN(value)) {
    return value;
  }
  
  // Converti a stringa e pulisci
  let str = String(value ?? '').trim();
  
  // Rimuovi simboli valuta e spazi
  str = str.replace(/[€$£\s]/g, '');
  
  // Stringa vuota -> default
  if (!str) {
    return defaultValue;
  }
  
  // Conta punti e virgole
  const dots = (str.match(/\./g) || []).length;
  const commas = (str.match(/,/g) || []).length;
  
  // Logica di parsing:
  // 1.543,50 -> 1543.50 (punto migliaia, virgola decimale)
  // 1,543.50 -> 1543.50 (virgola migliaia, punto decimale)
  // 1.543 -> 1543 (solo punto come migliaia se >= 3 cifre dopo)
  // 1,50 -> 1.50 (virgola decimale)
  // 1.50 -> 1.50 (punto decimale)
  
  if (commas === 1 && dots >= 1) {
    // Formato italiano: 1.234,56 -> punto migliaia, virgola decimale
    str = str.replace(/\./g, ''); // Rimuovi punti migliaia
    str = str.replace(',', '.'); // Virgola -> punto decimale
  } else if (dots === 1 && commas >= 1) {
    // Formato anglosassone: 1,234.56 -> virgola migliaia, punto decimale
    str = str.replace(/,/g, ''); // Rimuovi virgole migliaia
  } else if (commas === 1 && dots === 0) {
    // Solo virgola: potrebbe essere decimale italiano (1,5 o 1000,50)
    str = str.replace(',', '.');
  } else if (dots === 1 && commas === 0) {
    // Solo punto: se ha <= 2 cifre dopo è decimale, altrimenti migliaia
    const parts = str.split('.');
    if (parts[1] && parts[1].length === 3 && parts[0].length >= 1 && parts[0].length <= 3) {
      // Potrebbe essere 1.500 (migliaia) - ambiguo, trattiamo come migliaia solo se >= 3 cifre dopo punto
      // Ma per sicurezza, se l'utente scrive 1.5, trattiamo come 1.5
      // Lasciamo come punto decimale (caso più comune per UX)
    }
    // Punto già corretto per parseFloat
  } else if (dots > 1) {
    // Più punti: sono separatori migliaia (1.234.567)
    str = str.replace(/\./g, '');
  } else if (commas > 1) {
    // Più virgole: sono separatori migliaia (1,234,567)
    str = str.replace(/,/g, '');
  }
  
  const parsed = parseFloat(str);
  
  if (isNaN(parsed)) {
    return defaultValue;
  }
  
  // Arrotonda a 4 decimali per evitare problemi floating point
  return Math.round(parsed * 10000) / 10000;
};

/**
 * Formatta un numero per visualizzazione italiana
 * 
 * @param {number} value - Il numero da formattare
 * @param {number} decimals - Numero di decimali (default: 2)
 * @param {boolean} forceDecimals - Forza sempre i decimali anche se zero
 * @returns {string} - Numero formattato in italiano
 */
export const formatItalianNumber = (value, decimals = 2, forceDecimals = false) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '';
  }
  
  const num = Number(value);
  
  // Se il numero è intero e non forziamo i decimali, mostra senza decimali
  if (!forceDecimals && Number.isInteger(num)) {
    return num.toLocaleString('it-IT');
  }
  
  return num.toLocaleString('it-IT', {
    minimumFractionDigits: forceDecimals ? decimals : 0,
    maximumFractionDigits: decimals
  });
};

/**
 * Normalizza l'input durante la digitazione
 * Permette caratteri validi per numeri italiani senza fare parsing aggressivo
 * 
 * @param {string} value - Il valore corrente dell'input
 * @returns {string} - Valore normalizzato (rimuove solo caratteri invalidi)
 */
export const normalizeNumericInput = (value) => {
  if (value === null || value === undefined) {
    return '';
  }
  
  let str = String(value);
  
  // Rimuovi caratteri non validi (permetti solo numeri, punto, virgola, segno meno iniziale)
  str = str.replace(/[^\d.,\-]/g, '');
  
  // Permetti solo un segno meno all'inizio
  if (str.indexOf('-') > 0) {
    str = str.replace(/-/g, '');
  }
  
  return str;
};

/**
 * Verifica se una stringa rappresenta un numero valido (anche parziale durante digitazione)
 * 
 * @param {string} value - Il valore da verificare
 * @returns {boolean} - true se è un pattern numerico valido
 */
export const isValidNumericInput = (value) => {
  if (value === '' || value === '-' || value === ',' || value === '.') {
    return true; // Permetti stati intermedi durante digitazione
  }
  
  // Pattern per numeri italiani validi (anche parziali)
  // Permette: 123, 1.234, 1.234,56, 1,5, -123, ecc.
  const pattern = /^-?[\d]*[.,]?[\d]*[.,]?[\d]*$/;
  return pattern.test(value);
};

/**
 * Converte il valore per il salvataggio (parsing finale)
 * Da usare onBlur o prima del submit
 * 
 * @param {string|number} value - Il valore da convertire
 * @param {object} options - Opzioni di parsing
 * @param {number} options.min - Valore minimo consentito
 * @param {number} options.max - Valore massimo consentito  
 * @param {number} options.defaultValue - Valore di default se vuoto/invalido
 * @param {boolean} options.allowEmpty - Permetti stringa vuota (restituisce null)
 * @returns {number|null} - Numero validato o null/default
 */
export const parseForSave = (value, options = {}) => {
  const { 
    min = null, 
    max = null, 
    defaultValue = 0,
    allowEmpty = false 
  } = options;
  
  // Stringa vuota
  if (value === '' || value === null || value === undefined) {
    return allowEmpty ? null : defaultValue;
  }
  
  const parsed = parseItalianNumber(value, allowEmpty ? null : defaultValue);
  
  if (parsed === null) {
    return allowEmpty ? null : defaultValue;
  }
  
  // Applica limiti
  let result = parsed;
  if (min !== null && result < min) {
    result = min;
  }
  if (max !== null && result > max) {
    result = max;
  }
  
  return result;
};

/**
 * Helper per gestire l'onChange di input numerici
 * Permette digitazione libera, restituisce la stringa raw
 * 
 * @param {Event} e - L'evento onChange
 * @returns {string} - Il valore normalizzato come stringa
 */
export const handleNumericInputChange = (e) => {
  const rawValue = e.target.value;
  return normalizeNumericInput(rawValue);
};

/**
 * Helper per gestire l'onBlur di input numerici
 * Fa il parsing e formattazione finale
 * 
 * @param {string} value - Il valore corrente
 * @param {object} options - Opzioni (vedi parseForSave)
 * @returns {object} - { numericValue, displayValue }
 */
export const handleNumericInputBlur = (value, options = {}) => {
  const numericValue = parseForSave(value, options);
  
  // Se allowEmpty e il valore è null, mantieni stringa vuota
  if (numericValue === null) {
    return { numericValue: null, displayValue: '' };
  }
  
  // Formatta per visualizzazione
  const displayValue = formatItalianNumber(numericValue, 2, false);
  
  return { numericValue, displayValue };
};

export default {
  parseItalianNumber,
  formatItalianNumber,
  normalizeNumericInput,
  isValidNumericInput,
  parseForSave,
  handleNumericInputChange,
  handleNumericInputBlur
};
